import { useEffect, useRef, useState } from "react";
import { atom, useAtom } from "jotai";

import { useCalcWorker } from "./hooks";
import { Toggle } from "../../ui";

type EventEntry = { type: string; data: unknown; timestamp: number; channel: string };
const evs = atom<EventEntry[]>([]);

export const WorkerDebugEvents = () => {
  const worker = useCalcWorker();
  const [events, setEvents] = useAtom(evs);
  const [logToConsole, setLogToConsole] = useState(false);
  const [paused, setPaused] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const logRef = useRef(logToConsole);
  const pausedRef = useRef(paused);

  useEffect(() => {
    logRef.current = logToConsole;
  }, [logToConsole]);

  useEffect(() => {
    pausedRef.current = paused;
    if (!paused) setExpanded(null);
  }, [paused]);

  useEffect(() => {
    const append = (channel: string, e: { type: string; data: unknown }) => {
      const entry: EventEntry = { ...e, timestamp: performance.now() + performance.timeOrigin, channel };
      if (logRef.current) console.log(`[${channel}]`, e.type, e.data);
      if (!pausedRef.current) setEvents((prev) => [entry, ...prev].slice(0, 500));
    };

    const unsubs = [
      worker.channels.on("grid/replicated", (data) => append("grid", worker.grid.debugDecode(data))),
      worker.channels.on("calc/outbound", (data) => append("calc", data)),
    ];

    return () => unsubs.forEach((unsub) => unsub());
  }, [worker, setEvents]);

  const clear = () => setEvents([]);

  return (
    <div className="flex flex-col gap-2 text-[11px] h-full">
      {/* Controls */}
      <div className="flex items-center justify-between gap-4 w-full border-b border-divider pb-2">
        <Toggle checked={logToConsole} onChange={(e) => setLogToConsole(e.target.checked)} label="Log to console" />
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            className={`text-[10px] px-2 py-1 rounded ${paused ? "bg-primary text-primary-foreground" : "bg-surface-2 hover:bg-surface-3 text-foreground-muted"}`}
          >
            {paused ? "Resume" : "Pause"}
          </button>
          <button
            type="button"
            onClick={clear}
            className="text-[10px] px-2 py-1 rounded bg-surface-2 hover:bg-surface-3 text-foreground-muted"
          >
            Clear ({events.length})
          </button>
        </div>
      </div>

      {/* Events list */}
      <div className="flex flex-col gap-0.5 font-mono overflow-y-auto flex-1 min-h-0 scrollbar-thin">
        {events.slice(0, 5).map((e, i) => (
          <EventRow
            key={e.timestamp}
            event={e}
            expanded={expanded === i}
            canExpand={paused}
            onToggle={() => paused && setExpanded(expanded === i ? null : i)}
          />
        ))}
      </div>
    </div>
  );
};

const EventRow = ({
  event,
  expanded,
  canExpand,
  onToggle,
}: {
  event: EventEntry;
  expanded: boolean;
  canExpand: boolean;
  onToggle: () => void;
}) => {
  const time = new Date(event.timestamp).toLocaleTimeString("en-US");
  const typeColor = getTypeColor(event.type);

  return (
    <div className="flex flex-col border-b border-divider/50 pb-1">
      <div className="flex gap-2 items-center px-1 py-0.5">
        <span className="text-foreground-muted text-[9px] shrink-0 tabular-nums">{time}</span>
        <span className={`text-[9px] px-1 rounded ${typeColor} shrink-0`}>{event.channel}</span>
        <span className="font-medium shrink-0">{event.type}</span>
        {canExpand && (
          <button
            type="button"
            onClick={onToggle}
            className="text-[9px] px-1.5 py-0.5 rounded bg-surface-2 hover:bg-surface-3 text-foreground-muted ml-auto"
          >
            {expanded ? "Collapse" : "Expand"}
          </button>
        )}
      </div>
      <pre
        className={`text-[9px] text-foreground-muted px-1 whitespace-pre-wrap break-all ${expanded ? "" : "line-clamp-3"}`}
      >
        {expanded ? JSON.stringify(event.data, null, 2) : compact((event.data ?? {}) as Record<string, unknown>)}
      </pre>
    </div>
  );
};

const compact = (x: Record<string, unknown>) =>
  Object.keys(x)
    .map((y) => `${y}: ${JSON.stringify(x[y])}\n`)
    .join("");

const getTypeColor = (type: string) => {
  if (type.includes("UPDATE")) return "bg-blue-500/20 text-blue-400";
  if (type.includes("SNAPSHOT")) return "bg-purple-500/20 text-purple-400";
  if (type.includes("error") || type.includes("failed")) return "bg-red-500/20 text-red-400";
  return "bg-surface-2 text-foreground-muted";
};
